import { useState } from "react"

import { capitalize } from "web/services/inflector"
import { formatAsDate } from "web/services/format"
import { todayInISODate } from "web/services/helpers"
import RangeDayPicker, {
  INITIAL_STATE as INITIAL_RANGE_PICKER_STATE,
} from "web/components/DayPicker/RangeDayPicker"
import DayPicker from "web/components/DayPicker/DayPicker"

import Radio from "web/components/Form/Controls/Radio"
import CheckBox from "web/components/Form/Controls/CheckBox"
import { Icon } from "web/components"

import MobileHeader from "./MobileHeader"
import OperatorRadio from "../OperatorRadio"
import { DropDownActionButtons } from "../Buttons"
import { getOperators } from "./helpers"

const SELECTION_METHOD_DAY_PICKER = "user_selected"
const SELECTION_METHODS = {
  range: [
    {
      prefix: <Icon name="calendar" outline />,
      value: SELECTION_METHOD_DAY_PICKER,
      label: "chosen period",
      className: "mb-4",
    },
    {
      prefix: <Icon name="clock" outline />,
      value: "7 days",
      label: "next 7 days",
    },
    {
      prefix: <Icon name="clock" outline />,
      value: "30 days",
      label: "next 30 days",
    },
    {
      prefix: <Icon name="clock" outline />,
      value: "90 days",
      label: "next 90 days",
    },
    {
      prefix: <Icon name="clock" outline />,
      value: "7 days ago",
      label: "last 7 days",
    },
    {
      prefix: <Icon name="clock" outline />,
      value: "30 days ago",
      label: "last 30 days",
    },
    {
      prefix: <Icon name="clock" outline />,
      value: "90 days ago",
      label: "last 90 days",
    },
  ],
  less_than: [
    {
      prefix: <Icon name="calendar" outline />,
      value: "user_selected",
      label: "chosen date",
      className: "mb-4",
    },
    {
      prefix: <Icon name="clock" outline />,
      value: "now",
      label: "today",
    },
  ],
  greater_than: [
    {
      prefix: <Icon name="calendar" outline />,
      value: "user_selected",
      label: "chosen date",
      className: "mb-4",
    },
    {
      prefix: <Icon name="clock" outline />,
      value: "now",
      label: "today",
    },
  ],
}

const userCanSelectDate = (formState) =>
  formState.selectionMethod.value === "user_selected"

const formUserSelectedDateMessage = (formData) => {
  if (formData.operator.value === "range") {
    const hasBothDates = formData.date.from && formData.date.to
    if (hasBothDates) {
      return `${formatAsDate({ value: formData.date.from })} -
      ${formatAsDate({ value: formData.date.to })}`
    }

    if (formData.date.from) {
      return `${formatAsDate({ value: formData.date.from })} -`
    }

    return "Please select a start date"
  }

  if (!formData.date) {
    return "Please select a date"
  }

  return formatAsDate({ value: formData.date })
}

const getFormDateMessage = (formData) => {
  if (userCanSelectDate(formData)) {
    return formUserSelectedDateMessage(formData)
  }

  const selectedMethod = SELECTION_METHODS[formData.operator.value].find(
    (o) => o.value === formData.selectionMethod.value
  )
  return capitalize(selectedMethod.label)
}

const getDateStateValue = (operator) => {
  if (operator.value === "range") {
    return INITIAL_RANGE_PICKER_STATE
  }
  return ""
}

const isEmptyOrNotEmptyOperator = (formState) =>
  ["empty", "not_empty"].includes(formState.operator.value)

function calculateCanSubmit(formState) {
  if (isEmptyOrNotEmptyOperator(formState)) {
    return true
  }

  const operatorAndSelectionMethodSet =
    formState.operator.value && formState.selectionMethod.value

  if (!userCanSelectDate(formState)) {
    return operatorAndSelectionMethodSet
  }

  if (formState.operator?.value === "range") {
    return (
      operatorAndSelectionMethodSet &&
      formState.date?.from &&
      formState.date?.to
    )
  }

  return operatorAndSelectionMethodSet && formState.date
}

export function conditionFromFormValue(field, formValue) {
  let value = formValue.selectionMethod
  let operator = formValue.operator?.value

  const operatorIsRange = operator === "range"

  if (formValue.selectionMethod?.value === SELECTION_METHOD_DAY_PICKER) {
    if (operatorIsRange) {
      value = [{ value: formValue.date.from }, { value: formValue.date.to }]
    } else {
      value = { value: formValue.date }
    }
  }

  if (
    operatorIsRange &&
    formValue.selectionMethod?.value !== SELECTION_METHOD_DAY_PICKER
  ) {
    operator = "relative_interval"
  }

  return {
    field_id: field.id,
    operator,
    value,
    match_blank: formValue.match_blank
      ? formValue.match_blank.value
      : undefined,
  }
}

const INITIAL_FORM_STATE = {
  operator: { value: "range" },
  selectionMethod: { value: SELECTION_METHOD_DAY_PICKER },
  date: INITIAL_RANGE_PICKER_STATE,
  match_blank: false,
}

function getInitialFormState(condition) {
  const conditionValue = condition.value
  const conditionOperator = condition.operator

  let operator =
    conditionOperator && conditionOperator !== "equal"
      ? { value: conditionOperator }
      : INITIAL_FORM_STATE.operator

  let selectionMethod = { value: SELECTION_METHOD_DAY_PICKER }
  let date =
    conditionValue && !Array.isArray(conditionValue)
      ? conditionValue.value
      : INITIAL_FORM_STATE.date

  if (conditionOperator === "range" && conditionValue) {
    date = {
      from: conditionValue[0].value,
      to: conditionValue[1].value,
      enteredTo: conditionValue[1].value,
    }
  }

  if (conditionOperator === "relative_interval") {
    operator = { value: "range" }
    date = null
  }

  if (conditionOperator !== "range" && !conditionValue) {
    date = todayInISODate()
  }

  if (
    conditionValue &&
    conditionValue.value &&
    (conditionValue.value === "now" || conditionValue.value.includes("days"))
  ) {
    selectionMethod = conditionValue // probably need to map to full object
    date = null
  }

  return {
    operator,
    selectionMethod,
    date,
    match_blank: condition.match_blank
      ? { value: condition.match_blank }
      : { value: false },
  }
}

export default function EditDateConditionDropDown({
  field,
  condition,
  onChange = () => {},
  onClear = () => {},
  onCancel = () => {},
}) {
  const [formState, setFormState] = useState(getInitialFormState(condition))

  const DatePickerComponent =
    formState.operator.value === "range" ? RangeDayPicker : DayPicker

  const canSubmit = calculateCanSubmit(formState)

  return (
    <div className="flex min-h-full flex-col justify-between p-6 md:min-h-md">
      <MobileHeader field={field} onCancel={onCancel} />
      <div className="flex flex-grow flex-col space-y-5 md:flex-row md:space-x-3 md:space-y-0">
        <div className="md:min-w-3xs">
          <OperatorRadio
            flexDirection="col"
            id="operator"
            name="operator"
            lookup_values={getOperators(field.kind)}
            value={formState.operator}
            onChange={(value) =>
              setFormState((prev) => ({
                ...INITIAL_FORM_STATE,
                date: getDateStateValue(value),
                operator: value,
                match_blank: prev.match_blank,
              }))
            }
          />
        </div>
        {!isEmptyOrNotEmptyOperator(formState) && (
          <>
            <div className="md:min-w-3xs">
              <Radio
                centerText={false}
                flexDirection="col"
                id="selection-method"
                name="selection-method"
                lookup_values={SELECTION_METHODS[formState.operator.value]}
                value={formState.selectionMethod}
                onChange={(value) =>
                  setFormState((prev) => ({
                    date: getDateStateValue(formState.operator),
                    operator: formState.operator,
                    selectionMethod: value,
                    match_blank: prev.match_blank,
                  }))
                }
              />
            </div>
            <div className="flex flex-col md:min-w-xs">
              <div className="mx-6 border-b-2 border-blue-600">
                {getFormDateMessage(formState)}
              </div>
              {userCanSelectDate(formState) && (
                <DatePickerComponent
                  value={formState.date}
                  onChange={(value) =>
                    setFormState((prev) => ({
                      ...prev,
                      date: value,
                    }))
                  }
                />
              )}
            </div>
          </>
        )}
      </div>
      <div className="flex flex-col space-y-4 text-sm">
        <div className="flex justify-end">
          <CheckBox
            inline={true}
            showValueLabel={false}
            label={`Include records without ${field.label}`}
            id="match-blanks"
            name="match-blanks"
            value={formState.match_blank}
            onChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                match_blank: value,
              }))
            }
          />
        </div>
        <DropDownActionButtons
          handleClear={() =>
            onClear({
              field_id: field.id,
              operator: { value: "range" },
              value: { value: null },
              match_blank: false,
            })
          }
          handleAdd={() => onChange(conditionFromFormValue(field, formState))}
          addDisabled={!canSubmit}
        />
      </div>
    </div>
  )
}
